import React, { useState, useEffect } from 'react';
import { Users, FileText, LogOut, Key, Menu, X, Store } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Layout = ({ children, currentUser, onLogout, currentPage, onNavigate }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed for mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // Auto-open sidebar on desktop
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const menuItems = [
    {
      title: 'Laporan Pembelian LPG PSO',
      icon: FileText,
      page: 'lpg-report',
      roles: ['Admin', 'Manajer']
    },
    {
      title: 'Manajemen Pangkalan',
      icon: Store,
      page: 'outlet-management',
      roles: ['Admin', 'Manajer', 'Checker']
    },
    {
      title: 'Manajemen Pengguna',
      icon: Users,
      page: 'user-management',
      roles: ['Admin']
    }
  ];

  const filteredMenu = menuItems.filter(item => 
    item.roles.includes(currentUser?.role)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Mobile Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Responsive */}
      <div 
        className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          ${sidebarOpen && !isMobile ? 'md:w-64' : 'md:w-20'}
          w-64 bg-gradient-to-b from-emerald-700 to-emerald-900 text-white 
          transition-all duration-300 flex flex-col fixed left-0 top-0 h-screen z-40
        `}
      >
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-emerald-600">
          {(sidebarOpen || isMobile) && (
            <div>
              <h1 className="font-bold text-lg">Portal LPG</h1>
              <p className="text-xs text-emerald-200">PSO Management</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-emerald-600 md:block"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-emerald-600">
          {sidebarOpen ? (
            <div>
              <p className="font-semibold text-sm">{currentUser?.nama}</p>
              <p className="text-xs text-emerald-200">{currentUser?.role}</p>
              <p className="text-xs text-emerald-300 truncate">{currentUser?.username}</p>
            </div>
          ) : (
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold">{currentUser?.nama?.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Menu Items - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredMenu.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.page;
            
            return (
              <button
                key={item.page}
                onClick={() => {
                  onNavigate(item.page);
                  if (isMobile) setSidebarOpen(false); // Close sidebar on mobile after navigation
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-emerald-600 text-white' 
                    : 'text-emerald-100 hover:bg-emerald-600/50'
                }`}
                title={!sidebarOpen ? item.title : ''}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {(sidebarOpen || isMobile) && <span className="text-sm font-medium">{item.title}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom Actions - Fixed at bottom */}
        <div className="flex-shrink-0 p-2 border-t border-emerald-600 space-y-1">
          <button
            onClick={() => {
              onNavigate('change-password');
              if (isMobile) setSidebarOpen(false);
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-emerald-100 hover:bg-emerald-600/50 transition-colors"
            title={!sidebarOpen ? 'Ubah Password' : ''}
          >
            <Key className="h-5 w-5 flex-shrink-0" />
            {(sidebarOpen || isMobile) && <span className="text-sm font-medium">Ubah Password</span>}
          </button>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-emerald-100 hover:bg-red-600 transition-colors"
            title={!sidebarOpen ? 'Logout' : ''}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {(sidebarOpen || isMobile) && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </div>

      {/* Mobile Top Bar */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 h-14 bg-emerald-700 text-white flex items-center px-4 z-20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
            className="text-white hover:bg-emerald-600 mr-3"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-bold text-base">Portal LPG PSO</h1>
          </div>
        </div>
      )}

      {/* Main Content - Responsive margins */}
      <div 
        className={`
          ${isMobile ? 'pt-14' : (sidebarOpen ? 'md:ml-64' : 'md:ml-20')}
          transition-all duration-300 min-h-screen overflow-auto
        `}
      >
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;